import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { API_BASE_URL } from '@/utils/api';

const MySwal = withReactContent(Swal);

// --- 1. DEFINISI INTERFACE ---
interface TrialApplicant {
  id: number;
  user: string;     // DB: full_name
  email: string;
  phone: string;    // DB: phone_number
  country: string;  // DB: country_code
  level: string;
  status: 'pending' | 'active' | 'rejected'; // Mapping dari DB: new, scheduled, rejected
  waLink?: string;  // DB: wa_link
}

// --- 2. HELPER UTILS ---
const getAvatarColor = (name: string) => {
  const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEEAD', '#D4A5A5', '#9B59B6'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) { hash = name.charCodeAt(i) + ((hash << 5) - hash); }
  return colors[Math.abs(hash) % colors.length];
};

const getInitials = (name: string) => {
  return name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase();
};

// --- 3. CSS MODERN ---
const trialStyles = `
  :root { --bg-soft: #f4f7f6; --text-dark: #2d3436; --text-muted: #636e72; --success: #00b894; --warning: #fdcb6e; --danger: #ff7675; }
  .trial-container { padding: 20px; background: var(--bg-soft); border-radius: 20px; min-height: 400px; font-family: 'Poppins', sans-serif; }
  .trial-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; }
  .title-group h3 { font-size: 1.8rem; font-weight: 800; color: var(--text-dark); margin: 0; }
  .glass-search { background: white; border-radius: 50px; padding: 8px 20px; box-shadow: 0 4px 15px rgba(0,0,0,0.03); border: 1px solid rgba(0,0,0,0.02); display: flex; align-items: center; width: 280px; }
  .glass-search input { border: none; outline: none; width: 100%; font-size: 0.95rem; }
  .table-wrapper { overflow-x: auto; scrollbar-width: none; } .table-wrapper::-webkit-scrollbar { display: none; }
  .modern-table { width: 100%; border-collapse: separate; border-spacing: 0 15px; }
  .modern-table thead th { font-size: 0.85rem; text-transform: uppercase; color: #b2bec3; font-weight: 700; padding: 0 20px 10px 20px; text-align: left; }
  .trial-row td { background: white; padding: 20px; vertical-align: middle; border-top: 1px solid rgba(0,0,0,0.01); border-bottom: 1px solid rgba(0,0,0,0.01); }
  .trial-row td:first-child { border-top-left-radius: 16px; border-bottom-left-radius: 16px; }
  .trial-row td:last-child { border-top-right-radius: 16px; border-bottom-right-radius: 16px; }
  .user-info-group { display: flex; align-items: center; gap: 12px; }
  .avatar-small { width: 42px; height: 42px; border-radius: 12px; color: white; font-weight: 700; display: flex; align-items: center; justify-content: center; font-size: 0.9rem; }
  .country-badge { font-size: 0.75rem; color: var(--text-muted); display: flex; align-items: center; gap: 5px; margin-top: 2px; }
  .contact-row { display: flex; align-items: center; gap: 8px; font-size: 0.85rem; color: var(--text-muted); margin-bottom: 4px; }
  .contact-row.email i { color: #fab1a0; } .contact-row.wa i { color: #00b894; }
  .level-badge { padding: 5px 12px; border-radius: 20px; font-size: 0.75rem; font-weight: 700; display: inline-block; }
  .bg-basic { background: #e3f2fd; color: #1976d2; } .bg-indep { background: #fff3e0; color: #f57c00; }
  .status-indicator { font-size: 0.85rem; font-weight: 600; display: flex; align-items: center; gap: 6px; }
  .st-pending { color: var(--warning); } .st-active { color: var(--success); } .st-rejected { color: var(--danger); }
  .dot { width: 8px; height: 8px; border-radius: 50%; }
  .action-btn { width: 38px; height: 38px; border-radius: 50%; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: 0.2s; font-size: 1rem; }
  .btn-approve { background: #e0f2f1; color: #00b894; } .btn-reject { background: #ffebee; color: #ff7675; }
  .info-text { font-size: 0.75rem; color: #b2bec3; font-style: italic; display: block; margin-top: 5px; }
`;

const TrialClass: React.FC = () => {
  const [trials, setTrials] = useState<TrialApplicant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const API_URL = `${API_BASE_URL}/admin/trials`;

  // --- FETCH DATA ---
  const fetchTrials = async () => {
    try {
      setLoading(true);
      const res = await fetch(API_URL);
      const data = await res.json();
      if(data.success) {
        // Map Database Fields to Frontend Interface
        const mappedData = data.data.map((item: any) => ({
          id: item.id,
          user: item.full_name,
          email: item.email,
          phone: item.phone_number,
          country: item.country_code,
          level: item.level,
          // Convert DB status (new, scheduled, rejected) to Frontend status
          status: item.status === 'new' ? 'pending' : (item.status === 'scheduled' ? 'active' : 'rejected'),
          waLink: item.wa_link
        }));
        setTrials(mappedData);
      }
    } catch (err) {
      console.error("Error fetching trials:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrials();
  }, []);

  // --- LOGIC APPROVE (Kirim Link WA) ---
  const handleApproveTrial = (id: number) => {
    MySwal.fire({
      title: '<h3 style="color:#2d3436; margin:0;">Setujui Trial Class?</h3>',
      html: `
        <p style="color:#636e72; font-size:0.9rem; margin-bottom:20px;">Masukkan Link Grup WhatsApp untuk peserta ini:</p>
        <input type="text" id="wa-link-input" class="swal2-input" placeholder="https://chat.whatsapp.com/..." style="font-size:0.9rem;">
      `,
      showCancelButton: true,
      confirmButtonText: 'Kirim & Setujui',
      confirmButtonColor: '#00b894',
      preConfirm: () => {
        const input = Swal.getPopup()?.querySelector('#wa-link-input') as HTMLInputElement;
        const value = input?.value;
        if (!value) Swal.showValidationMessage('Harap masukkan Link Grup WhatsApp!');
        return value;
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
            const response = await fetch(`${API_URL}/${id}/approve`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ waLink: result.value })
            });
            const resJson = await response.json();
            
            if(resJson.success) {
                setTrials(trials.map((t) => (t.id === id ? { ...t, status: 'active', waLink: result.value } : t)));
                MySwal.fire({ icon: 'success', title: 'Berhasil!', text: 'Peserta disetujui & Link disimpan.', confirmButtonColor: '#00b894', timer: 2000 });
            }
        } catch (err) {
            MySwal.fire('Error', 'Gagal update data', 'error');
        }
      }
    });
  };

  // --- LOGIC REJECT ---
  const handleRejectTrial = (id: number) => {
    MySwal.fire({ 
        title: 'Tolak Pendaftaran?', text: "Status akan berubah menjadi Ditolak.",
        icon: 'warning', iconColor: '#ff7675', showCancelButton: true, 
        confirmButtonText: 'Ya, Tolak', confirmButtonColor: '#ff7675'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
            const response = await fetch(`${API_URL}/${id}/reject`, { method: 'PUT' });
            const resJson = await response.json();
            
            if(resJson.success) {
                setTrials(trials.map((t) => (t.id === id ? { ...t, status: 'rejected' } : t)));
                MySwal.fire({ icon: 'success', title: 'Ditolak', text: 'Status peserta diperbarui.', confirmButtonColor: '#ff7675', timer: 1500 });
            }
        } catch (err) {
            MySwal.fire('Error', 'Gagal update data', 'error');
        }
      }
    });
  };

  const filteredTrials = trials.filter(t => t.user.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <>
      <style>{trialStyles}</style>
      <div className="trial-container">
        <div className="trial-header">
            <div className="title-group">
                <h3>Trial Class</h3>
                <span>Kelola pendaftaran siswa baru.</span>
            </div>
            <div className="glass-search">
                <i className="fas fa-search" style={{color:'#74b9ff', marginRight:'10px'}}></i>
                <input type="text" placeholder="Cari pendaftar..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </div>
        </div>

        {loading ? (
             <div style={{textAlign:'center', padding:'40px', color:'#b2bec3'}}>Memuat data pendaftar...</div>
        ) : (
        <div className="table-wrapper">
          <table className="modern-table">
            <thead>
              <tr><th>Kandidat</th><th>Kontak</th><th>Level Peminatan</th><th>Status</th><th>Aksi</th></tr>
            </thead>
            <tbody>
              {filteredTrials.map((trial) => (
                <tr key={trial.id} className="trial-row">
                  <td>
                    <div className="user-info-group">
                        <div className="avatar-small" style={{background: getAvatarColor(trial.user)}}>{getInitials(trial.user)}</div>
                        <div className="text-wrapper">
                            <h4>{trial.user}</h4>
                            <div className="country-badge"><i className="fas fa-globe-americas"></i> {trial.country}</div>
                        </div>
                    </div>
                  </td>
                  <td>
                    <div className="contact-row email" title="Email"><i className="fas fa-envelope"></i> {trial.email}</div>
                    <div className="contact-row wa" title="WhatsApp"><i className="fab fa-whatsapp"></i> {trial.phone}</div>
                  </td>
                  <td>
                    <span className={`level-badge ${trial.level === 'Basic' ? 'bg-basic' : 'bg-indep'}`}>{trial.level}</span>
                  </td>
                  <td>
                    {trial.status === 'pending' && <div className="status-indicator st-pending"><div className="dot" style={{background:'#fdcb6e'}}></div> Menunggu Review</div>}
                    {trial.status === 'active' && (
                        <div>
                            <div className="status-indicator st-active"><div className="dot" style={{background:'#00b894'}}></div> Disetujui</div>
                            <span className="info-text">Link WA Terkirim</span>
                        </div>
                    )}
                    {trial.status === 'rejected' && <div className="status-indicator st-rejected"><div className="dot" style={{background:'#ff7675'}}></div> Ditolak</div>}
                  </td>
                  <td>
                    {trial.status === 'pending' ? (
                      <div style={{display:'flex', gap:'8px'}}>
                        <button className="action-btn btn-approve" onClick={() => handleApproveTrial(trial.id)} title="Terima & Kirim Link"><i className="fas fa-check"></i></button>
                        <button className="action-btn btn-reject" onClick={() => handleRejectTrial(trial.id)} title="Tolak"><i className="fas fa-times"></i></button>
                      </div>
                    ) : (
                      <div style={{color: '#b2bec3', fontSize:'0.9rem'}}><i className="fas fa-lock"></i> Selesai</div>
                    )}
                  </td>
                </tr>
              ))}
              {filteredTrials.length === 0 && (
                <tr><td colSpan={5} style={{textAlign:'center', padding:'40px', color:'#b2bec3'}}>Tidak ada data pendaftaran.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </>
  );
};

export default TrialClass;